import { getAuth, onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";

// ============================================================================
// Token Caching Utilities (for API calls)
// ============================================================================

// Constants for authentication and caching
const TOKEN_LIFETIME = 60 * 60 * 1000; // 60 minutes
// Refresh a little before the token's real `exp` so a request never goes out
// on an already-expired token while the refresh is still in flight.
const TOKEN_EXPIRY_MARGIN = 5 * 60 * 1000; // 5 minutes

let authPromise = null;
let tokenCache = {
  token: null,
  expiration: null,
};

// Derive the cache TTL from the Firebase ID token's own `exp` claim (minus a
// safety margin) instead of a flat wall-clock window. A flat `now + LIFETIME`
// re-stamps a mid-life token as valid for a fresh 60 min, so it gets served
// past its real `exp` and 401s — and because the cache short-circuits the
// getIdToken() call, Firebase's silent auto-refresh (which only fires when
// <5 min to expiry) never runs. Tying the TTL to `exp` keeps the cache from
// ever outliving the real token, so refresh happens on schedule. Falls back to
// the flat lifetime if the token can't be decoded — never throw in this path.
const getTokenExpiration = (token, now) => {
  try {
    const payloadB64 = token
      .split(".")[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const payload = JSON.parse(atob(payloadB64));
    if (payload?.exp) {
      return payload.exp * 1000 - TOKEN_EXPIRY_MARGIN;
    }
  } catch {
    // Unparseable token — fall through to the flat-lifetime fallback.
  }
  return now + TOKEN_LIFETIME;
};

// Singleton pattern for waitForUser to avoid creating new listeners frequently
export const waitForUser = () => {
  if (authPromise) return authPromise;

  authPromise = new Promise((resolve, reject) => {
    const authInstance = getAuth();
    if (authInstance.currentUser) {
      resolve(authInstance.currentUser);
      return;
    }

    const unsubscribe = authInstance.onAuthStateChanged((user) => {
      unsubscribe();
      if (user) resolve(user);
      else reject(new Error("No user found"));
    });
  });

  authPromise.catch(() => {
    authPromise = null;
  });

  return authPromise;
};

// Get token with caching
export const getAuthToken = async () => {
  const now = Date.now();

  // Check memory cache
  if (
    tokenCache.token &&
    tokenCache.expiration &&
    now < tokenCache.expiration
  ) {
    return tokenCache.token;
  }

  // Fetch new token
  const user = await waitForUser();
  const token = await user.getIdToken();
  if (!token) {
    throw new Error("No token found");
  }

  tokenCache = {
    token,
    expiration: getTokenExpiration(token, now),
  };

  return token;
};

// Synchronously return a cached, still-valid token, or null. Unlike
// getAuthToken this never awaits Firebase — it only reads the in-memory
// cache. Use it where an `await` is impossible, e.g. a
// `pagehide` / `visibilitychange` handler that must dispatch a keepalive
// request before the page unloads (an async token fetch would never resolve
// in time). Returns null when no unexpired token is cached, including on the
// first request after a reload, when any prior in-memory token is gone and the
// caller must fall back to the async getAuthToken path.
export const getCachedAuthToken = () => {
  const now = Date.now();
  if (tokenCache.token && tokenCache.expiration && now < tokenCache.expiration) {
    return tokenCache.token;
  }
  return null;
};

// Firebase error codes that mean the user's refresh token is no longer valid
// (admin revoked it, account was disabled, password changed elsewhere).
// On any of these, surface a graceful "session ended" toast and sign out.
const REVOKED_TOKEN_ERROR_CODES = new Set([
  "auth/user-token-expired",
  "auth/id-token-revoked",
  "auth/user-disabled",
  "auth/user-not-found",
  "auth/invalid-user-token",
  "auth/invalid-refresh-token",
]);

// Refresh token and update cache
export const refreshAuthToken = async () => {
  const user = await waitForUser();
  let newToken;
  try {
    // Force a real refresh — `getIdToken()` without args returns the cached
    // token, which is exactly what got us a 401 in the first place.
    newToken = await user.getIdToken(true);
  } catch (error) {
    if (REVOKED_TOKEN_ERROR_CODES.has(error?.code)) {
      // Dynamic import breaks the circular dep: sessionRevoked imports back
      // from this module for clearAuthCache.
      const { handleSessionRevoked } = await import("./sessionRevoked");
      handleSessionRevoked();
    }
    throw error;
  }
  if (!newToken) {
    throw new Error("No token found");
  }

  const now = Date.now();
  tokenCache = {
    token: newToken,
    expiration: getTokenExpiration(newToken, now),
  };

  return newToken;
};

// Clear auth cache (useful for logout)
export const clearAuthCache = () => {
  authPromise = null;
  tokenCache = {
    token: null,
    expiration: null,
  };
};

/**
 * Subscribes to Firebase auth state changes.
 * Returns an unsubscribe function.
 */
export const subscribeToAuthChanges = (callback) => {
  return onAuthStateChanged(auth, callback);
};
